<template>
  <div v-if="visible" class="modal-backdrop-custom" @click.self="onCancel" role="dialog" aria-modal="true" aria-labelledby="request-update-title">
    <div class="modal-dialog modal-dialog-centered request-update-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="request-update-title">Request update</h5>
          <button type="button" class="close-btn" @click="onCancel" aria-label="Close">
            <font-awesome-icon icon="fa-solid fa-times" />
          </button>
        </div>

        <div class="modal-body">
          <p class="lead-text">
            This till is on <strong>v{{ currentVersion }}</strong> — store latest is
            <strong>v{{ latestVersion }}</strong>. Tell support to update this till and
            we'll get a technician to you.
          </p>

          <div class="payload-card">
            <div class="payload-row">
              <span class="payload-label">Store</span>
              <span class="payload-value">{{ storeName || storeDB }}</span>
            </div>
            <div class="payload-row">
              <span class="payload-label">Device</span>
              <span class="payload-value">{{ deviceName }}</span>
            </div>
            <div class="payload-row">
              <span class="payload-label">App</span>
              <span class="payload-value">{{ appName }}</span>
            </div>
            <div class="payload-row">
              <span class="payload-label">Current version</span>
              <span class="payload-value">v{{ currentVersion }}</span>
            </div>
            <div class="payload-row">
              <span class="payload-label">Latest version</span>
              <span class="payload-value">v{{ latestVersion }}</span>
            </div>
          </div>

          <div class="form-group mt-3">
            <label for="request-update-contact" class="form-label">
              Contact number <span class="text-danger">*</span>
            </label>
            <input
              id="request-update-contact"
              type="tel"
              v-model="contact"
              class="form-control"
              :class="{ 'is-invalid': contactError }"
              placeholder="e.g. +260 97 123 4567"
              autocomplete="tel"
              @input="clearContactError"
              @blur="validateContact"
            />
            <div v-if="contactError" class="invalid-feedback">{{ contactError }}</div>
            <div class="form-text">
              Please enter a valid phone number including country code.
            </div>
          </div>

          <div class="form-group">
            <label for="request-update-name" class="form-label">
              Your name <span class="text-danger">*</span>
            </label>
            <input
              id="request-update-name"
              type="text"
              v-model="contactName"
              class="form-control"
              :class="{ 'is-invalid': nameError }"
              placeholder="e.g. Jane Mwape"
              autocomplete="name"
              @input="clearFieldErrors"
              @blur="validateName"
            />
            <div v-if="nameError" class="invalid-feedback">{{ nameError }}</div>
          </div>

          <div class="form-group">
            <label for="request-update-position" class="form-label">
              Position / Role <span class="text-danger">*</span>
            </label>
            <input
              id="request-update-position"
              type="text"
              v-model="contactPosition"
              class="form-control"
              :class="{ 'is-invalid': positionError }"
              placeholder="e.g. Manager, Supervisor, Cashier"
              autocomplete="organization-title"
              @input="clearFieldErrors"
              @blur="validatePosition"
            />
            <div v-if="positionError" class="invalid-feedback">{{ positionError }}</div>
          </div>

          <div v-if="submitError" class="alert alert-danger mt-3" role="alert">
            {{ submitError }}
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="onCancel" :disabled="submitting">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="onSubmit" :disabled="submitting">
            <span v-if="submitting" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
            {{ submitting ? 'Sending…' : 'Send request' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Storage } from '@/services/Storage';

/**
 * Modal for sending a "Request update" ticket from the login screen.
 *
 * Backend: POST {window.ticketApi}ticket/createSimplifiedTicket
 *   header: DB = base64(storeDB)
 *   body  : { type: 'calloutRequest', title, platform: 'Lytning POS',
 *             description, allStores: false, customerContact,
 *             contactName, contactPosition }
 *
 * Filename-on-the-wire maps to the existing TicketRequest.vue form
 * (point-of-sale/TicketRequest.vue:141-167) — same endpoint, same
 * header/body shape. Pre-login users are not authenticated; the endpoint
 * only needs the DB header, so a direct POST is the simplest path.
 *
 * Caller (UnifiedLoginView) passes the data captured from
 * POST /check-device-version — supports sees the same five fields the
 * spec requires (store, device name, app, current version, latest version),
 * plus the contact's name and role for triage.
 */
export default {
  name: 'RequestUpdateModal',
  props: {
    visible: { type: Boolean, default: false },
    storeDB: { type: String, default: '' },
    storeName: { type: String, default: '' },
    deviceName: { type: String, required: true },
    appName: { type: String, required: true },
    currentVersion: { type: String, required: true },
    latestVersion: { type: String, required: true }
  },
  emits: ['close', 'submitted'],
  data() {
    return {
      contact: '',
      contactName: '',
      contactPosition: '',
      contactError: '',
      nameError: '',
      positionError: '',
      submitting: false,
      submitError: ''
    };
  },
  watch: {
    visible(next) {
      if (next) {
        this.contact = '';
        this.contactName = '';
        this.contactPosition = '';
        this.contactError = '';
        this.nameError = '';
        this.positionError = '';
        this.submitError = '';
        this.submitting = false;
      }
    }
  },
  methods: {
    validateContact() {
      const phoneRegex = /^(\+?260|0)?[0-9]{9}$/;
      const trimmed = (this.contact || '').trim();
      const clean = trimmed.replace(/\s+/g, '');
      if (!trimmed) {
        this.contactError = 'Contact number is required';
        return false;
      }
      if (!phoneRegex.test(clean)) {
        this.contactError = 'Please enter a valid Zambian phone number (e.g. +260 97 123 4567 or 097 123 4567)';
        return false;
      }
      this.contactError = '';
      return true;
    },
    validateName() {
      const trimmed = (this.contactName || '').trim();
      if (!trimmed) {
        this.nameError = 'Your name is required';
        return false;
      }
      this.nameError = '';
      return true;
    },
    validatePosition() {
      const trimmed = (this.contactPosition || '').trim();
      if (!trimmed) {
        this.positionError = 'Your position is required';
        return false;
      }
      this.positionError = '';
      return true;
    },
    clearContactError() {
      if (this.contactError) this.contactError = '';
      if (this.submitError) this.submitError = '';
    },
    clearFieldErrors() {
      if (this.nameError) this.nameError = '';
      if (this.positionError) this.positionError = '';
      if (this.submitError) this.submitError = '';
    },
    onCancel() {
      if (this.submitting) return;
      this.$emit('close');
    },
    ticketApi() {
      if (typeof window === 'undefined' || !window.ticketApi) {
        throw new Error('RequestUpdateModal: window.ticketApi is not configured');
      }
      return window.ticketApi;
    },
    async resolveStoreDB() {
      // Prefer the canonical source used by every other ticket sender
      // (Storage.getCurrentStoreDB) — it waits for Vuex hydration and
      // falls back to the Electron hardware service if the register is
      // unavailable. Fall back to the prop if Storage resolves empty.
      let db = '';
      try {
        db = (await Storage.getCurrentStoreDB()) || '';
      } catch (_) {
        db = '';
      }
      db = (db || this.storeDB || '').toString().trim();
      if (!db) {
        // eslint-disable-next-line no-console
        console.warn('[RequestUpdateModal] storeDB unresolved; Vuex defaultStore keys:',
          this.$store && this.$store.getters && this.$store.getters.getDefaultStore
            ? Object.keys(this.$store.getters.getDefaultStore)
            : null);
      }
      return db;
    },
    buildBody(storeDB) {
      const store = this.storeName || storeDB;
      return {
        type: 'calloutRequest',
        title: `Update request — ${this.deviceName} (${this.appName}) v${this.currentVersion}`,
        platform: 'Lytning POS',
        description:
          `Till ${this.deviceName} runs ${this.appName} v${this.currentVersion}; ` +
          `store latest is v${this.latestVersion}. ` +
          `Store: ${store}. Requested via login screen.`,
        allStores: false,
        customerContact: this.contact.trim(),
        contactName: this.contactName.trim(),
        contactPosition: this.contactPosition.trim()
      };
    },
    async onSubmit() {
      if (this.submitting) return;
      const validContact = this.validateContact();
      const validName = this.validateName();
      const validPosition = this.validatePosition();
      if (!validContact || !validName || !validPosition) return;

      this.submitting = true;
      this.submitError = '';

      const storeDB = await this.resolveStoreDB();
      if (!storeDB) {
        this.submitting = false;
        this.submitError = 'Could not identify your store. Please close this dialog and try again.';
        return;
      }

      try {
        const encoded = window.btoa(storeDB);
        const response = await axios.post(
          `${this.ticketApi()}ticket/createSimplifiedTicket`,
          this.buildBody(storeDB),
          { headers: { DB: encoded } }
        );
        this.$emit('submitted', response && response.data);
        this.$emit('close');
      } catch (err) {
        const serverMessage = err && err.response && err.response.data
          && (err.response.data.message || err.response.data.Message);
        this.submitError = serverMessage
          || (err && err.message)
          || 'Could not send the update request. Please try again.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.request-update-modal {
  width: 100%;
  max-width: 480px;
  margin: 0;
}

.modal-content {
  background: var(--card-bg);
  color: var(--text-primary);
  border: var(--card-border);
  border-radius: 14px;
  box-shadow: var(--card-shadow-hover, 0 8px 24px rgba(0, 0, 0, 0.18));
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: var(--card-border);
}

.modal-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);
}

.close-btn {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  line-height: 1;
}

.close-btn:hover {
  background: var(--bg-secondary, rgba(0, 0, 0, 0.05));
  color: var(--text-primary);
}

.modal-body {
  padding: 1.25rem;
  color: var(--text-primary);
}

.lead-text {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: var(--text-primary);
}

.payload-card {
  background: var(--bg-secondary, rgba(0, 0, 0, 0.03));
  border: var(--card-border);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.payload-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  gap: 1rem;
}

.payload-label {
  color: var(--text-secondary);
  font-weight: 500;
}

.payload-value {
  color: var(--text-primary);
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.form-label {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.85rem;
  margin-bottom: 0.4rem;
}

.form-control {
  background: var(--card-bg);
  color: var(--text-primary);
  border: var(--card-border);
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  font-size: 0.95rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--accent-electric-blue);
  box-shadow: 0 0 0 3px rgba(51, 156, 255, 0.15);
}

.form-control.is-invalid {
  border-color: var(--danger);
}

.invalid-feedback {
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.form-text {
  color: var(--text-secondary);
  font-size: 0.78rem;
  margin-top: 0.25rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1rem;
  border-top: var(--card-border);
}

.btn {
  min-height: 40px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: var(--accent-electric-blue);
  border: 1px solid var(--accent-electric-blue);
  color: var(--text-on-accent, #fff);
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.05);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-outline-secondary {
  background: transparent;
  border: 1px solid var(--card-border);
  color: var(--text-primary);
}

.btn-outline-secondary:hover:not(:disabled) {
  background: var(--bg-secondary, rgba(0, 0, 0, 0.04));
}

.spinner-grow-sm {
  width: 0.9rem;
  height: 0.9rem;
}

.alert-danger {
  background: var(--danger-bg, rgba(217, 83, 79, 0.1));
  color: var(--danger);
  border: 1px solid var(--danger);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  font-size: 0.85rem;
}

.text-danger {
  color: var(--danger);
}
</style>
